import { useState } from "react";
import { Navigate } from "react-router-dom";
import Particles from "react-tsparticles";
import { loadFull } from "tsparticles";
import useAuth from "../hooks/useAuth";
import { Button } from "../ui-components/button-with-loading";
import { ArrowRightOnRectangleIcon } from "@heroicons/react/24/outline";
import newsquawkLogo from "../assets/newsquawk-logo-white.png";
import { PricingModal } from "../ui-components/PricingModal";

const particlesInit = async (main) => {
  await loadFull(main);
};

const Login = () => {
  const { authenticated, keycloakInstance } = useAuth();
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  const handleLogin = () => {
    keycloakInstance.login();
  };

  if (authenticated) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Particle background */}
      <Particles
        id="login-particles"
        init={particlesInit}
        options={{
          fullScreen: { enable: false },
          background: {
            color: {
              value: "#030712",
            },
          },
          particles: {
            number: {
              value: 70,
              density: {
                enable: true,
                area: 800,
              },
            },
            color: {
              value: "#ffffff",
            },
            shape: {
              type: "circle",
            },
            opacity: {
              value: 0.5,
            },
            size: {
              value: { min: 1, max: 3 },
            },
            move: {
              enable: true,
              speed: 1,
              direction: "none",
              random: false,
              straight: false,
              outModes: {
                default: "out",
              },
            },
            links: {
              enable: true,
              distance: 150,
              color: "#ffffff",
              opacity: 0.4,
              width: 1,
            },
          },
          interactivity: {
            events: {
              onHover: {
                enable: true,
                mode: "repulse",
              },
              onClick: {
                enable: true,
                mode: "push",
              },
              resize: true,
            },
            modes: {
              repulse: {
                distance: 100,
              },
              push: {
                quantity: 4,
              },
            },
          },
          detectRetina: true,
        }}
        className="absolute inset-0"
      />

      {/* Login card */}
      <div className="relative z-10 w-full max-w-md px-6">
        <div
          className="bg-gray-900 border border-gray-800 rounded-2xl p-10 text-center"
          style={{
            boxShadow:
              "0 0 0 1px rgba(255,255,255,0.05), 0 25px 50px -12px rgba(0,0,0,0.5)",
          }}
        >
          <img
            src={newsquawkLogo}
            alt="Newsquawk"
            className="w-48 h-auto mx-auto mb-8"
          />

          {/* Divider */}
          <div
            className="h-px mb-8"
            style={{
              background:
                "linear-gradient(90deg, transparent, #1f2937, transparent)",
            }}
          />

          <h1 className="text-2xl font-bold text-white mb-3 tracking-tight">
            Welcome Back
          </h1>
          <p className="text-gray-400 text-sm leading-relaxed mb-8">
            Sign in to access real-time market intelligence and breaking
            financial news.
          </p>

          <Button
            size="lg"
            onClick={handleLogin}
            className="w-full flex items-center justify-center gap-2"
            style={{
              boxShadow: "0 0 20px rgba(0, 174, 239, 0.3)",
            }}
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            Sign In
          </Button>

          <p className="mt-8 text-xs text-gray-500">
            Don&apos;t have an account?{" "}
            <button
              onClick={() => setIsPricingOpen(true)}
              className="text-squawk-blue hover:underline"
            >
              View Plans
            </button>
          </p>
          <p className="mt-3 text-xs text-gray-500">
            <a
              href="https://legacy.newsquawk.com/users/sign_in"
              target="_blank"
              rel="noopener noreferrer"
              className="text-squawk-blue hover:underline"
            >
              Click here for legacy Newsquawk
            </a>
          </p>
        </div>
      </div>

      <PricingModal
        isOpen={isPricingOpen}
        onClose={() => setIsPricingOpen(false)}
      />
    </div>
  );
};

export default Login;
